import React, { useState, useMemo } from "react";
import { Calculator, Send } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SectionCTA from "@/components/SectionCTA";
import api from "@/lib/api";
import { IMG, AI_DISCLAIMER } from "@/data/site";

const FORMATS = [
  { label: "60 x 120 cm", area: 0.72 },
  { label: "30 x 60 cm", area: 0.18 },
  { label: "60 x 60 cm", area: 0.36 },
  { label: "120 x 278 cm slab", area: 3.336 },
  { label: "10 x 30 cm metro", area: 0.03 },
];

export default function MaterialCalculator() {
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [hei, setHei] = useState("");
  const [surface, setSurface] = useState("floor");
  const [format, setFormat] = useState(FORMATS[0].label);
  const [wastage, setWastage] = useState(10);
  const [contact, setContact] = useState({ name: "", email: "" });

  const calc = useMemo(() => {
    const l = parseFloat(len) || 0, w = parseFloat(wid) || 0, h = parseFloat(hei) || 0;
    const floor = l * w;
    const walls = 2 * (l + w) * h;
    const area = surface === "floor" ? floor : surface === "walls" ? walls : floor + walls;
    const withWaste = area * (1 + wastage / 100);
    const tileArea = FORMATS.find((f) => f.label === format)?.area || 0.72;
    return {
      area: area.toFixed(2),
      withWaste: withWaste.toFixed(2),
      tiles: tileArea ? Math.ceil(withWaste / tileArea) : 0,
      adhesive: (withWaste * 4).toFixed(1),
      grout: (withWaste * 0.5).toFixed(1),
    };
  }, [len, wid, hei, surface, format, wastage]);

  const email = async () => {
    if (!contact.name || !contact.email) { toast.error("Add your name and email to send results."); return; }
    try {
      await api.post("/enquiries", {
        kind: "quote", name: contact.name, email: contact.email,
        message: "Material calculator estimate request",
        payload: { surface, tile_format: format, dimensions_m: `${len}x${wid}x${hei}`, wastage_pct: wastage, area_m2: calc.area, tiles_needed: calc.tiles, adhesive_kg: calc.adhesive, grout_kg: calc.grout },
      });
      toast.success("Estimate sent to Rossi Ceramics");
    } catch (e) { toast.error("Could not send. Please try again."); }
  };

  return (
    <div data-testid="calculator-page">
      <Seo title="Tile Material Calculator" description="Estimate tile quantities, adhesive and grout for your project with the Rossi Ceramics material calculator." />
      <PageHero eyebrow="Plan your project" title="Material calculator" subtitle="Estimate tiles, adhesive and grout in seconds. All results are estimates for planning only." image={IMG.marble[0]} />

      <section className="container-rossi py-16 grid lg:grid-cols-2 gap-10 max-w-5xl">
        <div className="card-lux p-7">
          <h2 className="font-serif text-2xl text-rossi-cream mb-6 flex items-center gap-2"><Calculator size={22} className="text-rossi-gold" /> Your room</h2>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[["Length (m)", len, setLen], ["Width (m)", wid, setWid], ["Height (m)", hei, setHei]].map(([ph, val, set]) => (
              <input key={ph} type="number" step="0.1" placeholder={ph} value={val} onChange={(e) => set(e.target.value)} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid={`calc-${ph.split(" ")[0].toLowerCase()}`} />
            ))}
          </div>

          <label className="text-sm text-rossi-cream/80 mb-2 block">Surface</label>
          <div className="flex gap-2 mb-5">
            {[["floor", "Floor"], ["walls", "Walls"], ["both", "Floor + Walls"]].map(([v, l]) => (
              <button key={v} onClick={() => setSurface(v)} className={`px-4 py-2 rounded-full text-sm ${surface === v ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80"}`}>{l}</button>
            ))}
          </div>

          <label className="text-sm text-rossi-cream/80 mb-2 block">Tile format</label>
          <select value={format} onChange={(e) => setFormat(e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-5 focus:outline-none focus:border-rossi-gold/50" data-testid="calc-format">
            {FORMATS.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
          </select>

          <label className="text-sm text-rossi-cream/80 mb-2 block">Wastage allowance: {wastage}%</label>
          <input type="range" min="5" max="20" value={wastage} onChange={(e) => setWastage(+e.target.value)} className="w-full accent-rossi-gold" data-testid="calc-wastage" />
        </div>

        <div className="card-lux p-7">
          <h2 className="font-serif text-2xl text-rossi-cream mb-6">Estimate</h2>
          <div className="space-y-4">
            {[["Surface area", `${calc.area} m²`], ["With wastage", `${calc.withWaste} m²`], ["Tiles / boxes needed", `${calc.tiles}`], ["Adhesive (approx)", `${calc.adhesive} kg`], ["Grout (approx)", `${calc.grout} kg`]].map(([l, v]) => (
              <div key={l} className="flex justify-between items-center border-b border-white/5 pb-3">
                <span className="text-sm text-rossi-cream/70">{l}</span>
                <span className="font-mono text-rossi-gold text-lg">{v}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-rossi-stone mt-5">{AI_DISCLAIMER}</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <input placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="calc-name" />
            <input placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="calc-email" />
          </div>
          <button onClick={email} className="btn-gold w-full mt-4" data-testid="calc-send"><Send size={16} /> Email results to Rossi Ceramics</button>
        </div>
      </section>

      <SectionCTA />
    </div>
  );
}
